import { Panel } from "../../components/panels/Panel";
import { useEffect, useRef, useState } from "react";
import { useSocket } from "../../contexts/SocketContext";
import {
  ARQUUS_OPERATIONS, ARQUUS_STATE_CURRENT, ARQUUS_STATE_ERROR,
  ARQUUS_STATE_STANDBY,
  ARQUUS_STATE_VALIDATED
} from "../../services/live-interface/arquus-interface-service";
import { SignalSlashIcon } from "@heroicons/react/24/outline";
import { useAlert } from "../../contexts/AlertContext";
import ArquusLogo from '../../assets/images/logos/Arquus_Logo.png';
import LogoPerception from "../../assets/images/logos/logo-blue-black.png";
import LogoRenault from "../../assets/images/logos/Renault_Logo.png";

const ArquusMessageBull = ({ currentStep, lastErrorStep }) => {
  const message = currentStep?.start_message ?? lastErrorStep?.error_message ?? lastErrorStep?.current_message ?? null;
  const isStart = currentStep?.start_message !== null && currentStep?.start_message !== undefined;

  const bubbleClass = isStart
    ? "text-white bg-yellow-500 before:bg-yellow-500"
    : "text-white bg-perception-error-500 before:bg-perception-error-500";

  return (
    message && (
      <div className="flex justify-center">
        <div
          className={`mx-8 relative p-8 text-7xl rounded-3xl
          before:content-[''] before:absolute before:-bottom-3 before:left-10
          before:w-12 before:h-12 before:rounded-bl-full before:rotate-45 ${bubbleClass}`}
        >
          {message}
        </div>
      </div>
    )
  );
};

export const ArquusLivePage = () => {

  const { socket, subscribeToEvent, unsubscribeFromEvent } = useSocket();
  const [currentData, setCurrentData] = useState([]);

  const { showAlert } = useAlert();

  const initStates = () => Object.fromEntries(ARQUUS_OPERATIONS.map(op => [op.name, ARQUUS_STATE_STANDBY]));
  const statesRef = useRef(initStates());

  const resetInterface = () => {
    statesRef.current = initStates();
    setCurrentData([]);
  };

  useEffect(() => {
    if (!socket) return;

    const handleReceivedData = (data) => {
      console.log(data);
      setCurrentData(data);

      if (data?.reset === true) {
        resetInterface();
        return;
      }

      if (data?.correction === true) {
        showAlert('success', 'Merci d\'avoir corrigé le système.');
        return;
      }

      /*
      pour chaque action
        si ses déclencheurs start et stop sont true
          si l'action est true -> state validé
          sinon si l'action est false -> state error
        sinon si le déclencheur start est true -> state current
       */

      for (const operation of ARQUUS_OPERATIONS) {
        const operationStart = operation.name + '_start';
        const operationEnd = operation.name + '_end';
        const operationDone = operation.name + '_done';

        if (data?.[operationStart] === true && data?.[operationEnd] === true) {
          if (data?.[operationDone] === true) {
            statesRef.current[operation.name] = ARQUUS_STATE_VALIDATED;
          }
          else {
            statesRef.current[operation.name] = ARQUUS_STATE_ERROR;
          }
        }
        else if (data?.[operationStart] === true) {
          statesRef.current[operation.name] = ARQUUS_STATE_CURRENT;

          if (operation.current_message !== null && data?.[operationDone] === false) {
            statesRef.current[operation.name] = ARQUUS_STATE_ERROR;
          }
        }
      }

    };

    subscribeToEvent('current_data', handleReceivedData);

    return () => {
      unsubscribeFromEvent('current_data', handleReceivedData);
    };
  }, [socket]);

  const currentStep = ARQUUS_OPERATIONS.find(op => statesRef.current[op.name] === ARQUUS_STATE_CURRENT);
  const lastErrorStep = (() => {
    const index = [...ARQUUS_OPERATIONS].map(op => statesRef.current[op.name]).lastIndexOf(ARQUUS_STATE_ERROR);
    if (index === -1) return undefined;
    const allZeroAfter = ARQUUS_OPERATIONS.slice(index + 1).every(op => statesRef.current[op.name] === ARQUUS_STATE_STANDBY);
    return allZeroAfter ? ARQUUS_OPERATIONS[index] : undefined;
  })();

  return (
    <div className="flex w-full h-full">
      <Panel title="GAMME OBSERVEE" category="secondary" width="w-2/5" noHeader={true} noPadding={true}>
        <div className="h-full flex flex-col gap-4 bg-gray-200 p-4">
          <h1 className="text-3xl text-perception-gray-700">Gamme : TTO T3</h1>
          {
            ARQUUS_OPERATIONS.map((operation, index) => {
              const textColor = statesRef.current[operation.name] === ARQUUS_STATE_STANDBY ? 'opacity-50' : '';
              const bgColor = statesRef.current[operation.name] === ARQUUS_STATE_CURRENT ? 'bg-perception-blue' :
                              statesRef.current[operation.name] === ARQUUS_STATE_VALIDATED ? 'bg-perception-success-600' :
                              statesRef.current[operation.name] === ARQUUS_STATE_ERROR ? 'bg-perception-error-500' : 'bg-perception-gray-400';
              return (
                <div key={index} className={`p-4 rounded-md text-white ${textColor} ${bgColor} text-2xl `}>
                  <span className="bg-black/50 px-2 py-1 mr-4 rounded-md text-white">{operation.id}</span>
                  {operation.display_text}
                </div>
              )
            })
          }
        </div>
      </Panel>
      <Panel title="OPERATION SUIVIE" category="main" width="w-3/5" noHeader={true} noPadding={true}>
        <div className="flex flex-col h-full bg-gray-100 p-4">
          <div className="flex-1 flex flex-col gap-8 justify-center items-center w-full h-full text-center">
            {
              currentStep === undefined && lastErrorStep === undefined ?
                <>
                  <div className='animate-pulse animate-pulse-grow'>
                    <SignalSlashIcon className='h-72 text-perception-gray-600' />
                  </div>
                  <p className="text-5xl text-perception-gray-600">Aucune action identifiée</p>
                </>
                :
                <div className="text-5xl flex flex-col gap-32 text-perception-gray-600">
                  <div>
                    Etape identifiée : <span className="text-perception-blue">
                      {currentStep !== undefined ? currentStep.display_text : lastErrorStep.display_text}
                    </span>
                  </div>
                  <ArquusMessageBull currentStep={currentStep} lastErrorStep={lastErrorStep} />
                </div>
            }
          </div>

          {/* Logos */}
          <div className="h-[50px] flex justify-center items-center text-xl gap-4 text-black">
            <img src={LogoPerception} alt="logo-perception" className="max-w-[200px] max-h-[50px]" />
            x
            <img src={ArquusLogo} alt="logo-Arquus" className="max-w-[200px] max-h-[50px]" />
          </div>
        </div>
      </Panel>
    </div>
  );
};